import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Input  from '../../components/ui/Input';
import Button from '../../components/ui/Button';

export default function LoginPage() {
  const { login, loading } = useAuth();

  const [form, setForm]     = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [showPass, setShowPass] = useState(false);

  // ── Field change ────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (apiError)     setApiError('');
  };

  // ── Client-side validation ───────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.email.trim())   errs.email    = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.password)       errs.password = 'Password is required';
    return errs;
  };

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    try {
      const result = await login({ email: form.email, password: form.password });
      if (!result.status) {
        setApiError(result.message || 'Login failed. Please try again.');
      }
      // On success AuthContext saves token → GuestRoute redirects to /dashboard
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.email?.[0] ||
        'Something went wrong. Please try again.';
      setApiError(msg);
    }
  };

  return (
    <div className="app-shell flex flex-col items-center justify-center min-h-screen px-6">

      {/* ── Brand header ── */}
      <div className="mb-10 text-center animate-fade-up">
        {/* Gold jewellery icon */}
        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full"
             style={{
               background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 40%, #f0c040 100%)',
               boxShadow: '0 8px 32px rgba(212, 160, 23, 0.45)',
             }}>
          <svg viewBox="0 0 40 40" fill="none" className="h-10 w-10" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 5L26 14H14L20 5Z" fill="white" fillOpacity="0.9"/>
            <path d="M14 14L10 20L20 35L30 20L26 14H14Z" fill="white" fillOpacity="0.75"/>
            <path d="M10 20L20 35L30 20" stroke="white" strokeWidth="1.2" strokeOpacity="0.5" fill="none"/>
            <circle cx="20" cy="14" r="2" fill="white" fillOpacity="0.95"/>
          </svg>
        </div>

        <h1 className="font-serif text-3xl font-bold text-gold-shimmer">
          Shine Jewellery
        </h1>
        <p className="mt-1 text-sm font-medium tracking-widest uppercase"
           style={{ color: 'rgba(212,160,23,0.55)' }}>
          Admin Portal
        </p>
      </div>

      {/* ── Login card ── */}
      <div className="card w-full animate-fade-up" style={{ animationDelay: '80ms' }}>

        <h2 className="mb-1 text-xl font-serif font-semibold text-white">
          Welcome back
        </h2>
        <p className="mb-6 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Sign in to your admin account
        </p>

        {/* API error banner */}
        {apiError && (
          <div className="mb-5 flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
               style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
            <svg className="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9V7h2v2H9zm0 2h2v4H9v-4z" clipRule="evenodd"/>
            </svg>
            <span>{apiError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">

          {/* Email */}
          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="admin@shinejewellery.com"
            value={form.email}
            onChange={handleChange}
            error={errors.email}
            autoComplete="email"
            autoFocus
          />

          {/* Password */}
          <div className="flex flex-col">
            <label className="input-label">Password</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                name="password"
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
                className={`input-field pr-12 ${errors.password ? 'border-red-400' : ''}`}
              />
              {/* Toggle password visibility */}
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-opacity hover:opacity-80"
                style={{ color: 'rgba(255,255,255,0.45)' }}
                tabIndex={-1}
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                {showPass ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-0.5 text-xs" style={{ color: '#fca5a5' }}>{errors.password}</p>
            )}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            variant="gold"
            fullWidth
            loading={loading}
            className="mt-1"
          >
            Sign In
          </Button>

        </form>
      </div>

      {/* Footer */}
      <p className="mt-8 text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
        © {new Date().getFullYear()} Shine Jewellery Investment. Admin access only.
      </p>

    </div>
  );
}
